import client from './client';

// All Relevance AI traffic goes through our own backend now.
//
// The browser used to POST straight to
// `api-<region>.stack.tryrelevance.com/.../trigger_limited_async`, which is
// unauthenticated. In Sep 2026 Relevance began resolving those public triggers
// against a tool's *published* version; none of ours have one, so every
// generator started failing with 404 "does not exist or is not publicly
// triggerable". The authenticated endpoints have no such requirement, and the
// key for them has to live server-side.
//
// Going through `/relevance/*` also means the browser only ever talks to our
// own domain, which is what corporate networks (NMCI and friends) allow.

export type RelevanceTriggerResponse = {
  job_id: string;
  project: string;
  studio_id: string;
};

export type RelevancePollUpdate = {
  type: string;
  output?: { output?: { answer?: string } };
};

export type RelevancePollResponse = {
  type: string;
  updates: RelevancePollUpdate[];
};

async function trigger(studioId: string, params: Record<string, unknown>) {
  const { data } = await client.post<RelevanceTriggerResponse>('/relevance/trigger', {
    studioId,
    params,
  });
  return data;
}

async function poll(studioId: string, jobId: string) {
  const { data } = await client.get<RelevancePollResponse>(
    `/relevance/poll/${encodeURIComponent(studioId)}/${encodeURIComponent(jobId)}`,
    { timeout: 30000 },
  );
  return data;
}

// A poll that fails for a reason that will never change — bad session, unknown
// studio, malformed job id — should stop the run. Anything else (timeout, 429,
// 5xx, connection dropped) is worth another attempt, because the job is already
// running and being paid for.
const isTerminalPollError = (error: any) => {
  const status = error?.response?.status;
  return status === 400 || status === 401 || status === 403 || status === 404;
};

// Convenience wrapper for the tools that used to use the synchronous
// `trigger_limited` endpoint. Polling happens here rather than on the server so
// a long run can never hit Heroku's 30 second router timeout.
async function run(
  studioId: string,
  params: Record<string, unknown>,
  { intervalMs = 3000, deadlineMs = 300000 }: { intervalMs?: number; deadlineMs?: number } = {},
) {
  const { job_id } = await trigger(studioId, params);
  const deadline = Date.now() + deadlineMs;

  let consecutiveErrors = 0;
  let lastError: any = null;

  while (Date.now() < deadline) {
    let data: RelevancePollResponse;

    try {
      data = await poll(studioId, job_id);
      consecutiveErrors = 0;
    } catch (error: any) {
      if (isTerminalPollError(error)) {
        throw error;
      }
      // The run is already under way upstream; give up on it only after the
      // deadline, never on a single hiccup, or we strand a job we paid for.
      lastError = error;
      consecutiveErrors += 1;
      const backoff = Math.min(intervalMs * 2 ** Math.min(consecutiveErrors, 4), 30000);
      if (Date.now() + backoff >= deadline) break;
      await new Promise((resolve) => setTimeout(resolve, backoff));
      continue;
    }

    const finished = data.updates?.find((update) => update.type === 'chain-success');

    if (finished) {
      const answer = finished.output?.output?.answer;
      if (!answer) {
        throw new Error('Invalid Relevance response: missing output.output.answer');
      }
      return { answer, fullResponse: data };
    }

    if (data.type === 'failed') {
      throw new Error('Relevance tool run failed');
    }

    if (Date.now() + intervalMs >= deadline) break;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error(
    lastError
      ? `Relevance tool run timed out; last polling error: ${lastError?.message ?? lastError}`
      : 'Relevance tool run timed out',
  );
}

export const relevance = {
  trigger,
  poll,
  run,
};
